import React, {useReducer, useState} from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import {Grid} from '@material-ui/core';
import Checkbox from './Checkbox';
import {
  INSTRUMENT_MAP,
  INSTRUMENT_NAME_MAP,
  DataExportFormat,
  getCurrentDate,
  downloadBlob,
  isSafari,
  isIOS,
  sleep,
} from './utils';

const instrumentCSVSupportMap = Object.freeze({
  bode: true,
  iv: true,
  scope: true,
  datalogger: true,
});

const instrumentTDMSSupportMap = Object.freeze({
  bode: false,
  iv: false,
  scope: true,
  datalogger: true,
});

const DISABLE_DATA_EXPORT_STATES = new Set(['starting', 'running', 'stopping']);

const clickInstrumentExportFormatReducer = (state, action) => {
  let {selectedInstrumentsExportFormats} = state;
  let {instrumentExportFormat} = action;
  if (selectedInstrumentsExportFormats.has(instrumentExportFormat)) {
    selectedInstrumentsExportFormats.delete(instrumentExportFormat);
  } else {
    selectedInstrumentsExportFormats.add(instrumentExportFormat);
  }
  return {
    ...state,
    selectedInstrumentsExportFormats: new Set(selectedInstrumentsExportFormats),
  };
};

const dataExportReducer = (state, action) => {
  switch (action.type) {
    case 'CLICK_INSTRUMENT_EXPORT_FORMAT':
      return clickInstrumentExportFormatReducer(state, action);
    default:
      return state;
  }
};

const useDataExportReducer = () => {
  const [state, dispatch] = useReducer(dataExportReducer, {
    selectedInstrumentsExportFormats: new Set(),
  });

  const clickInstrumentExportFormat = (instrumentExportFormat) =>
    dispatch({type: 'CLICK_INSTRUMENT_EXPORT_FORMAT', instrumentExportFormat});

  return {
    state,
    clickInstrumentExportFormat,
  };
};

const DataExportForm = ({instruments, toolbarRef, deviceConnected, isSimulateMode}) => {
  const {
    state: {selectedInstrumentsExportFormats},
    clickInstrumentExportFormat,
  } = useDataExportReducer();

  const instrumentsWrapper = toolbarRef.current
    ? toolbarRef.current.closest('.bentobox-wrapper').querySelector('.instruments-wrapper')
    : null;
  const [warningMessage, setWarningMessage] = useState('');

  const exportData = async () => {
    if (isIOS) {
      setWarningMessage('iOS does not allow exporting data.');
      return;
    }
    const {year, month, day, hour, minute, second} = getCurrentDate();

    const results = await Promise.all(
      Array.from(selectedInstrumentsExportFormats).map((exportItem) => {
        const [instrumentName, format] = exportItem.split('-');
        const instrumentEl = instrumentsWrapper.querySelector(INSTRUMENT_MAP[instrumentName]);
        return instrumentEl && instrumentEl.exportDataAsync
          ? Promise.all([
              instrumentName,
              format,
              instrumentEl.exportDataAsync(DataExportFormat[format], instrumentName),
            ])
          : Promise.resolve(null);
      }),
    ).then((results) => results.filter((result) => result != null));

    for (const [name, format, blob] of results) {
      const fileName = `${name}_${year}${month}${day}_${hour}${minute}${second}.${format}`;
      if (isSafari) {
        downloadBlob(blob, fileName);
        await sleep(1000);
      } else {
        downloadBlob(blob, fileName);
      }
    }
  };

  const noInstrumentRunning = () => {
    const instrumentNames = new Set();
    for (const exportItem of selectedInstrumentsExportFormats) {
      const [instrumentName] = exportItem.split('-');
      instrumentNames.add(instrumentName);
    }
    return Array.from(instrumentNames).reduce(
      (allIdle, instrumentName) =>
        !(instrumentsWrapper && instrumentsWrapper.querySelector(INSTRUMENT_MAP[instrumentName]))
          ? allIdle
          : allIdle &&
            !DISABLE_DATA_EXPORT_STATES.has(instrumentsWrapper.querySelector(INSTRUMENT_MAP[instrumentName]).state),
      true,
    );
  };

  const exportDisabled = selectedInstrumentsExportFormats.size <= 0 || !noInstrumentRunning();

  const exportFileDisabled = (instrumentName, disableExportWhenNotConnect) => {
    if (!instrumentsWrapper) {
      return false;
    }
    const instrumentElement = instrumentsWrapper.querySelector(INSTRUMENT_MAP[instrumentName]);
    const disableWhenNotConnect = disableExportWhenNotConnect ? !deviceConnected : false;
    if (instrumentElement && instrumentElement.disableExportFileToWeb) {
      return instrumentElement.disableExportFileToWeb() || disableWhenNotConnect;
    } else {
      return disableWhenNotConnect;
    }
  };

  return (
    <Grid container spacing={16} direction="column" className="data-export-instruments">
      <Grid item xs={12} className="title">
        Choose data to export
      </Grid>
      {[...instruments.large, ...instruments.small]
        .map(({name}) => {
          const supportCSV = instrumentCSVSupportMap[name];
          const supportTDMS = instrumentTDMSSupportMap[name];
          const isExportCSVDisabled = exportFileDisabled(name, false);
          const isExportTDMSDisabled = isSimulateMode ? true : exportFileDisabled(name, true);
          const csvName = `${name}-csv`;
          const tdmsName = `${name}-tdms`;
          return !supportCSV && !supportTDMS ? null : (
            <Grid key={name} item xs={12} className="export-options-wrapper">
              <div className="instrument-name">{INSTRUMENT_NAME_MAP[name]}</div>
              <div className="export-options">
                {supportCSV && (
                  <span
                    onClick={isExportCSVDisabled ? undefined : () => clickInstrumentExportFormat(csvName)}
                    className={classNames({
                      'export-option': true,
                      disabled: isExportCSVDisabled,
                    })}
                  >
                    <Checkbox selected={selectedInstrumentsExportFormats.has(csvName)} /> CSV
                  </span>
                )}
                {supportTDMS && (
                  <span
                    onClick={isExportTDMSDisabled ? undefined : () => clickInstrumentExportFormat(tdmsName)}
                    className={classNames({
                      'export-option': true,
                      disabled: isExportTDMSDisabled,
                    })}
                  >
                    <Checkbox selected={selectedInstrumentsExportFormats.has(tdmsName)} /> TDMS
                  </span>
                )}
              </div>
            </Grid>
          );
        })
        .filter((node) => node !== null)}
      <Grid item xs={12}>
        <button disabled={exportDisabled} onClick={exportData}>
          Export Data
        </button>
      </Grid>
      <Grid item xs={12}>
        <span className="warning-item">{warningMessage}</span>
      </Grid>
    </Grid>
  );
};

DataExportForm.propTypes = {
  instruments: PropTypes.object.isRequired,
  toolbarRef: PropTypes.object.isRequired,
  deviceConnected: PropTypes.bool.isRequired,
  isSimulateMode: PropTypes.bool.isRequired,
};

export default DataExportForm;
